import BftButton from "common/components/button";
import CountriesDropdown from "common/components/countries-dropdown";
import BftInput from "common/components/input";
import MessageModal from "common/components/message-modal";
import OtpModal from "common/components/otp-modal";
import { firstStepSchema } from "common/validators/validators";
import { COUNTRIES_PREFIX } from "constants/countries";
import { authAuthorize, authRecuperaPraticaEmail, authVerifyOTP } from "core/auth/_redux/action";
import { authUserSelector } from "core/auth/_redux/selectors";
import { AppRouterService } from "core/routes/app-router.service";
import { PathEnum } from "core/routes/path.enum";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { Formik, FormikProps } from "formik";
import { UserFormValues } from "models/login";
import { useRef, useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const title = "Inserisci i dati richiesti";

const otpMessage = "Il codice di attivazione è stato inviato all' e-mail inserita.";

const exceptThisSymbols = ["e", "E", "+", "-", ".", ","];

const CreazioneProfilo: React.FC = () => {
	const formRef = useRef<FormikProps<UserFormValues>>(null);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const user = useAppSelector(authUserSelector);

	const [showModalOtp, setShowModalOtp] = useState<boolean>(false);
	const [showModalRecupera, setShowModalRecupera] = useState<boolean>(false);

	const handleShowRecupera = () => {
		setShowModalRecupera(true);
	};

	const handleShowChiudi = () => {
		setShowModalRecupera(false);

		if (formRef?.current?.values?.email)
			dispatch(authRecuperaPraticaEmail(formRef?.current?.values?.email));
	};

	const privacyLabel = () => {
		return (
			<p>
				Accetto integralmente il trattamento dei dati personali funzionali all'utilizzo del
				Servizio, come disciplinato nel presenti&nbsp;
				<a
					href={process.env.PUBLIC_URL + "/assets/TerminiECondizioni.pdf"}
					style={{ cursor: "pointer" }}
					target="_blank"
				>
					Termini e condizioni
				</a>
				.
			</p>
		);
	};

	const goNext = () => {
		navigate(AppRouterService.get(PathEnum.codiceAttivazione));
	};

	const handleSubmit = (values?: UserFormValues) => {
		dispatch(authAuthorize(values))
			.unwrap()
			.then((res) => {
				if (res && !showModalOtp) setShowModalOtp(true);
			})
			.catch((e) => {
				handleShowRecupera();
			});
	};

	const handleOtpSubmit = (input: string) => {
		dispatch(authVerifyOTP(Number(input)))
			.unwrap()
			.then((res) => {
				if (res?.idToken) {
					setShowModalOtp(false);
					goNext();
				}
			});
	};

	return (
		<>
			<Formik
				innerRef={formRef}
				validationSchema={firstStepSchema}
				onSubmit={handleSubmit}
				initialValues={
					{
						firstName: user?.firstName || "",
						lastName: user?.lastName || "",
						email: user?.email || "",
						prefix: user?.prefix || "+39",
						phoneNumber: user?.phoneNumber || "",
						privacyCheck: user?.privacyCheck,
					} as UserFormValues
				}
			>
				{({
					handleSubmit: _handleSubmit,
					handleChange,
					setFieldValue,
					values,
					touched,
					errors,
				}) => (
					<form onSubmit={_handleSubmit}>
						<Card className="shadow">
							<Card.Body>
								<h4 className="m-3 mb-4">{title}</h4>
								<BftInput
									required
									type={"text"}
									name={"firstName"}
									placeholder={"Nome Legale Rappresentante*"}
									value={values.firstName.toUpperCase()}
									onChange={(e) => {
										const { name, value } = e.target;
										setFieldValue(name, value.toUpperCase());
									}}
									isInvalid={!!errors.firstName && touched.firstName}
								/>
								{errors.firstName && touched.firstName && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors.firstName}
									</Form.Control.Feedback>
								)}

								<BftInput
									required
									type={"text"}
									name={"lastName"}
									placeholder={"Cognome Legale Rappresentante*"}
									value={values.lastName.toUpperCase()}
									onChange={(e) => {
										const { name, value } = e.target;
										setFieldValue(name, value.toUpperCase());
									}}
									isInvalid={!!errors.lastName && touched.lastName}
								/>
								{errors.lastName && touched.lastName && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors.lastName}
									</Form.Control.Feedback>
								)}

								<BftInput
									required
									type={"email"}
									name={"email"}
									placeholder={"Email*"}
									value={values.email}
									onChange={handleChange}
									isInvalid={!!errors.email && touched.email}
								/>
								{errors.email && touched.email && (
									<Form.Control.Feedback className="ms-3" type="invalid">
										{errors.email}
									</Form.Control.Feedback>
								)}

								<Row className="mb-3 justify-content-between">
									<Col md={3}>
										<CountriesDropdown
											options={COUNTRIES_PREFIX}
											containerClass="mb-3"
											value={values.prefix}
											onChange={handleChange("prefix")}
										/>
									</Col>
									<Col md={9}>
										<BftInput
											required
											type={"number"}
											name={"phoneNumber"}
											placeholder={"Telefono Legale Rappresentante*"}
											value={values.phoneNumber}
											onPaste
											onKeyDown={(e) => exceptThisSymbols.includes(e.key) && e.preventDefault()}
											onChange={handleChange}
											isInvalid={!!errors.phoneNumber && touched.phoneNumber}
										/>
										{errors.phoneNumber && touched.phoneNumber && (
											<Form.Control.Feedback className="ms-3" type="invalid">
												{errors.phoneNumber}
											</Form.Control.Feedback>
										)}
									</Col>
								</Row>

								<div className="d-flex gray-container">
									<Form.Check
										className="mx-3 my-3"
										type="checkbox"
										name="privacyCheck"
										value={"true"}
										isInvalid={!!errors.privacyCheck && touched.privacyCheck}
										feedback={
											touched.privacyCheck && errors.privacyCheck ? (
												<Form.Control.Feedback className="mt-3" type="invalid">
													{errors.privacyCheck}
												</Form.Control.Feedback>
											) : null
										}
										feedbackType="invalid"
										onChange={handleChange}
										label={privacyLabel()}
									/>
								</div>
							</Card.Body>
						</Card>

						<div className="mx-3">
							<BftButton className="btn-buffetti col-12" type="submit">
								Avanti
							</BftButton>
						</div>
					</form>
				)}
			</Formik>

			<MessageModal
				show={showModalRecupera}
				message={getPratica()}
				onClose={handleShowChiudi}
				title="Pratica già esistente"
			/>

			<OtpModal
				show={showModalOtp}
				message={otpMessage}
				onSubmit={handleOtpSubmit}
				handleClose={() => {
					setShowModalOtp(false);
				}}
				onResend={handleSubmit}
			/>
		</>
	);
};

const getPratica = () => {
	return (
		<p>
			Gentile cliente, risulta una pratica in corso associata alla sua e-mail. Può riprendere la sua
			pratica dall'apposita funzionalità "Recupera la tua pratica interrotta!".
		</p>
	);
};

export default CreazioneProfilo;
